/* eslint-disable no-console */
import { getCookie, setCookie } from 'Utils/session';
import { getUtmParamsFromUrl } from 'Utils/utm';
import { getItem, setItem } from 'Utils/storage';

const UTM_STORAGE_KEY = 'utm_values';
const UTM_TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

export function useUtmParams() {
  const storeUtmParamsFromUrl = (url: string) => {
    const utmParams = getUtmParamsFromUrl(url);

    if (Object.keys(utmParams).length > 0 && !getCookie(UTM_STORAGE_KEY)) {
      setCookie(UTM_STORAGE_KEY, encodeURIComponent(JSON.stringify(utmParams)), 90, '/');
    }
  };

  const getUtmParamsFromCookie = (): Record<string, string> => {
    const utmParamsCookie = getCookie(UTM_STORAGE_KEY);
    if (!utmParamsCookie) return {};
    // Backward/forward compatible parse: try plain JSON, then URL-decoded JSON.
    try {
      return JSON.parse(utmParamsCookie);
    } catch {
      try {
        return JSON.parse(decodeURIComponent(utmParamsCookie));
      } catch {
        return {};
      }
    }
  };

  const saveUtmValuesToLocalStorage = (utmValues: Record<string, string>) => {
    try {
      const savedValue = getItem(UTM_STORAGE_KEY);
      const existingValues = savedValue ? JSON.parse(savedValue) : {};
      // Reset on new definitive attribution signal to avoid mixing sets.
      const isNewAttribution = ['gclid', 'msclkid', 'fbclid', 'utm_source'].some(k => Object.prototype.hasOwnProperty.call(utmValues, k));
      const base = isNewAttribution ? {} : existingValues;
      const newValues = { ...base, ...utmValues, _ts: Date.now() };
      setItem(UTM_STORAGE_KEY, JSON.stringify(newValues));
    } catch (error) {
      console.error('Error saving UTM values to localStorage:', error);
    }
  };

  const getUtmValuesFromLocalStorage = () => {
    try {
      const savedValue = getItem(UTM_STORAGE_KEY);
      const raw = savedValue ? JSON.parse(savedValue) : {};
      const ts = typeof raw._ts === 'number' ? raw._ts : 0;
      // Missing/invalid timestamps are considered expired to avoid indefinite retention.
      if (!ts || Date.now() - ts > UTM_TTL_MS) {
        localStorage.removeItem(UTM_STORAGE_KEY);
        return {};
      }
      const { ...values } = raw;
      return values;
    } catch (error) {
      console.error('Error getting UTM values from localStorage:', error);
      return {};
    }
  };

  return {
    storeUtmParamsFromUrl,
    getUtmParamsFromCookie,
    saveUtmValuesToLocalStorage,
    getUtmValuesFromLocalStorage,
  };
}
